import { FC, useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import {
  Row,
  Col,
  Text,
  Icon,
  Checkbox,
  Button,
  ControlledForm,
  ControlledSelectCard,
  ControlledInput,
  Collapse,
  CollapseItem,
  Content,
  Image
} from '@/client/components'
import { shopDomain } from '@/client/context/Config/ConfigProvider'
import { useGetPrices } from '@/client/hooks'
import { getOptionsField } from '@/client/submodules/Calculator/helper'
import { api } from '@/client/utils'
import { schemaResolver, minValue, maxWidth, maxHeight } from './asserts'
import { FileChecker } from './FileChecker'
import * as S from './styles'
import { OnlinePreflightProps, FormSchema } from '../types'

export const OnlinePreflight: FC<OnlinePreflightProps> = ({
  dieline_generator,
  shape,
  dimension,
  diameter,
  width,
  height,
  options,
  opaque,
  foil,
  download_button,
  asserts_group,
  file_checker,
  sorts_title,
  sorts,
  upload_button,
  upload_text,
  drag_and_drop,
  how_does_it_work_title,
  how_does_it_work_content,
  how_to_use_title,
  how_to_use_content,
  tutorial_title,
  tutorial_content,
  preflight_mistakes,
  professional_assistance,
  artwork_service_long_text,
  artwork_service_button,
  artwork_service_image,
  preflight_success,
  success_long_text,
  calculator_button,
  calculator_link,
  artwork_service_link,
  first_waiting_text,
  second_waiting_text,
  login_model
}) => {
  const { locale, defaultLocale } = useRouter()
  const { data } = useGetPrices(locale)
  const [error, setError] = useState('')
  const [triangle, setTriangle] = useState(false)
  const [opaqueCheckbox, setOpaqueCheckbox] = useState(false)
  const [foilCheckbox, setFoilCheckbox] = useState(false)

  const formMethods = useForm<FormSchema>({
    resolver: schemaResolver(asserts_group),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      shape: '',
      diameter: '',
      height: '',
      width: '',
      opaque: '',
      foil: ''
    }
  })
  const { isDirty, isValid } = formMethods.formState

  const idMapping = {
    54: 1,
    55: 2,
    56: 3
  }

  const updatedData = getOptionsField(data?.calculatorFields, 'label_shape').map((item) => ({
    ...item,
    id: idMapping[item.id] ?? item.id
  }))

  const handleCheckbox = useCallback(
    (name) => {
      if (name === 'opaque') {
        const newValue = opaqueCheckbox ? '0' : '1'
        setOpaqueCheckbox(!opaqueCheckbox)
        formMethods.setValue('opaque', newValue, { shouldDirty: true })
      }

      if (name === 'foil') {
        const newValue = foilCheckbox ? '0' : '1'
        setFoilCheckbox(!foilCheckbox)
        formMethods.setValue('foil', newValue, { shouldDirty: true })
      }
    },
    [opaqueCheckbox, foilCheckbox]
  )

  const handleSubmitForm = useCallback(() => {
    const fields: Array<'shape' | 'width' | 'height' | 'foil' | 'opaque'> = [
      'shape',
      'width',
      'height',
      'foil',
      'opaque'
    ]
    const newSelection = fields.reduce((acc, field) => {
      acc[field] = +formMethods.getValues(field)
      return acc
    }, {})

    const shape = +formMethods.getValues('shape')
    const diameter = +formMethods.getValues('diameter')
    if (diameter > 0 && shape === 2) {
      newSelection['width'] = diameter
      newSelection['height'] = diameter
    }

    downloadAIFile(newSelection)
  }, [formMethods])

  const downloadAIFile = async (data) => {
    try {
      const url = `/artwork/dieline-generator?height=${data['height']}&width=${data['width']}&shape=${
        data['shape']
      }&addHPI=${data['opaque']}&addFoil=${data['foil']}&lang=${defaultLocale === 'uk' ? 'en' : defaultLocale}`
      const response = await api.get(url)

      const base64Data = response.data.ai_file_b64
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Uint8Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const blob = new Blob([byteNumbers], { type: 'application/octet-stream' })
      const fileName = `${shopDomain}_template_${data['width']}x${data['height']}.ai`
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      setError('Failed to download the AI file')
    }
  }

  return (
    <>
      <S.Featured>
        <Row>
          <Col>
            <S.MainFeatured>
              <FileChecker
                file_checker={file_checker}
                dimension={dimension}
                width={width}
                height={height}
                options={options}
                opaque={opaque}
                foil={foil}
                sorts_title={sorts_title}
                sorts={sorts}
                asserts_group={asserts_group}
                upload_button={upload_button}
                upload_text={upload_text}
                drag_and_drop={drag_and_drop}
                how_does_it_work_title={how_does_it_work_title}
                how_does_it_work_content={how_does_it_work_content}
                how_to_use_content={how_to_use_content}
                triangle_visible={triangle}
                preflight_mistakes={preflight_mistakes}
                professional_assistance={professional_assistance}
                artwork_service_long_text={artwork_service_long_text}
                artwork_service_button={artwork_service_button}
                artwork_service_image={artwork_service_image}
                preflight_success={preflight_success}
                success_long_text={success_long_text}
                calculator_button={calculator_button}
                calculator_link={calculator_link}
                artwork_service_link={artwork_service_link}
                first_waiting_text={first_waiting_text}
                second_waiting_text={second_waiting_text}
                login_model={login_model}
              />

              <S.DieGenerator onMouseEnter={() => setTriangle(true)} onMouseLeave={() => setTriangle(false)}>
                <S.TopTriangle className='triangle' isVisible={triangle} />

                <S.CardHeader>
                  <Text size='lg' family='heading' weight='bold' margin='0' color='inverse' padding='0.8rem  2.5rem'>
                    <Icon name='dieline' size='s5' />
                    {dieline_generator}
                  </Text>
                </S.CardHeader>

                <S.Form>
                  <ControlledForm formMethods={formMethods}>
                    <S.Block>
                      <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                        {shape}
                      </Text>

                      <S.Option>
                        <ControlledSelectCard<FormSchema> label='shape' items={updatedData} />
                      </S.Option>
                    </S.Block>

                    {+formMethods.getValues('shape') === 2 ? (
                      <S.Block>
                        <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                          {diameter}
                        </Text>
                        <S.Option>
                          <ControlledInput
                            {...formMethods.register('diameter')}
                            dimension='large'
                            type='number'
                            min={minValue}
                            max={maxHeight}
                            label={`${diameter} (mm)`}
                            value={formMethods.getValues('diameter')}
                            onChange={(e) => formMethods.setValue('diameter', e.target.value)}
                          />
                        </S.Option>
                      </S.Block>
                    ) : (
                      <S.Block>
                        <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                          {dimension}
                        </Text>
                        <S.Option>
                          <>
                            <ControlledInput
                              {...formMethods.register('width')}
                              dimension='large'
                              type='number'
                              min={minValue}
                              max={maxWidth}
                              label={width}
                              value={formMethods.getValues('width')}
                              onChange={(e) => formMethods.setValue('width', e.target.value)}
                            />

                            <ControlledInput
                              {...formMethods.register('height')}
                              dimension='large'
                              type='number'
                              min={minValue}
                              max={maxHeight}
                              label={height}
                              value={formMethods.getValues('height')}
                              onChange={(e) => formMethods.setValue('height', e.target.value)}
                            />
                          </>
                        </S.Option>
                      </S.Block>
                    )}

                    <S.Block>
                      <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                        {options}
                      </Text>
                      <S.Option>
                        <Checkbox
                          id='opaqueDie'
                          value={opaqueCheckbox ? '1' : '0'}
                          label={opaque}
                          checked={opaqueCheckbox}
                          kind='square'
                          {...formMethods.register('opaque')}
                          onChange={() => handleCheckbox('opaque')}
                        />
                        <Checkbox
                          id='foilDie'
                          value={foilCheckbox ? '1' : '0'}
                          label={foil}
                          checked={foilCheckbox}
                          kind='square'
                          {...formMethods.register('foil')}
                          onChange={() => handleCheckbox('foil')}
                        />
                      </S.Option>
                    </S.Block>

                    <Button kind='primary' disabled={!isDirty || !isValid} onClick={handleSubmitForm}>
                      <Icon name='download-new' />
                      {download_button}
                    </Button>
                    <Text color='danger' size='xs'>
                      {error}
                    </Text>
                  </ControlledForm>
                </S.Form>

                <Collapse>
                  <CollapseItem key={how_to_use_title} title={how_to_use_title}>
                    <Content
                      content={how_to_use_content}
                      style={{ fontSize: '0.85rem', lineHeight: '1.4', color: 'black' }}
                    />
                  </CollapseItem>
                </Collapse>

                <S.DividerLine />

                <Collapse>
                  <CollapseItem key={tutorial_title} title={tutorial_title}>
                    {tutorial_content?.map(({ content, image }) => (
                      <S.ContentInfos key={`key_${image.url}`}>
                        <S.ContentLeft>
                          <Content
                            content={content}
                            style={{ fontSize: '0.85rem', lineHeight: '1.4', color: 'black', paddingRight: '1rem' }}
                          />
                        </S.ContentLeft>

                        <S.ContentRight>
                          <Image
                            alt={image.alt}
                            src={image.url}
                            width={image.width}
                            height={image.height}
                            className='tutorial_image'
                          />
                        </S.ContentRight>
                      </S.ContentInfos>
                    ))}
                  </CollapseItem>
                </Collapse>

                <S.DividerLine />
              </S.DieGenerator>
            </S.MainFeatured>
          </Col>
        </Row>
      </S.Featured>
    </>
  )
}
